/**
 * PREREG_004 — THE DESK'S READING (R1297), FOLDED INTO THE INK.
 *
 * R1297 (DESK-n43, 2026-09-02) is *"the interpretation the contract reserves to the desk"*, and it
 * closed with an instruction to this seat: **"HUDCHAT folds the badge states into ORDER 77's land,
 * reading this file; the desk does not touch the app."** This module is that fold, and the reading
 * document it is folded FROM is `docs/research/PREREG_004_DESK_READING_2026-09-02.md`.
 *
 * ⛔ THE APP DOES NOT INTERPRET. The verdicts below are DECLARED, transcribed from the desk's row —
 * they are NOT re-derived here from the numbers. Under AGENTS.md law 6 the reading is the desk's
 * act; a client that recomputed a verdict would be a second interpreter, free to drift from the
 * first, and the drift would surface as ink the desk never authorised.
 *
 * ⚠ SO WHAT ARMS THE TRANSCRIPTION? Not the verdict — a declared constant cannot falsify itself.
 * The CELLS are transcribed beside it, and `readingIsSelfConsistent()` checks the declared verdict
 * against R1297's own stated grounds ("amber: both forms below baseline in both halves → DEAD";
 * "violet: absolute +0.026 in TRAIN … → HYPOTHESIS"). A typo in either the verdict OR the cells
 * breaks the agreement between them, and the arm goes red. That is the only falsifier available to
 * a transcription, and it is a real one: it fails in BOTH directions.
 *
 * ⛔ AND THE CLASS-LEVEL PROPERTY, ARMED POSITIVELY: **NOTHING FLIPS TO CALIBRATED.** R1297's
 * law-6 sentence is *"no cell CALIBRATES"*. A negative list ("amber is not calibrated, violet is
 * not calibrated") arms the two instances I happened to think of; `NOTHING_CALIBRATES` states the
 * property over the whole verdict table, so a marker ADDED later without a desk reading cannot
 * slip a CALIBRATED badge past the gate by simply not being on my list.
 *
 * 📌 The pre-PREREG_004 chips this replaces carried live-looking rates — "64.7% follows · n=17",
 * "SNAP · 72.7% · +7.1t @15m · n=22" — on the amber and violet ghosts. R1297 kills amber outright
 * (n = 1,291 / 1,002 against a baseline it never beats) and leaves violet a HYPOTHESIS resting on a
 * single TRAIN cell that did not clear α. Shipping "64.7% follows" beside a DEAD verdict would be
 * the hub asserting a rate the desk has just retired, which is exactly what law 6 forbids.
 */

/** The three states law 6 allows a marker to hold. There is no fourth, and no marker is CALIBRATED. */
export type StudyVerdict = "DEAD" | "HYPOTHESIS" | "CALIBRATED";

/** The ghost line's two directions, named as `linkedStudyModel.ts` names them (`className`). */
export type GhostClass = "amber" | "violet";

/** `percentile` and `absolute` are R1297's two FORMS — the reading calls this the *filter*. */
export type ReadingForm = "percentile" | "absolute";

/** TRAIN / HOLDOUT are the pre-registered halves — the reading calls this the *era*. */
export type ReadingHalf = "TRAIN" | "HOLDOUT";

export type ReadingCell = {
  readonly marker: GhostClass;
  readonly form: ReadingForm;
  readonly half: ReadingHalf;
  /** Scored APPEAR events with `is_rth == 1` (reading rule R5). */
  readonly n: number;
  readonly hitRate: number;
  readonly baseline: number;
  /** `hitRate - baseline`, transcribed as the desk published it rather than recomputed. */
  readonly delta: number;
  /** One-sided circular-block bootstrap p (reading rule R2). α = 0.0253. */
  readonly p: number;
};

/**
 * H2-U, verbatim from the reading document's table — amber (+, CVD above price) and violet (−).
 *
 * ⚠ THE SIGN CONVENTION IS LOAD-BEARING AND IT IS CHECKED, NOT ASSUMED. The reading names amber
 * "(+, CVD above price)"; `linkedStudyModel.ts` mints `state = normalized > 0 ? "amber" : "violet"`
 * where `normalized` is `(shadow - price) / scale` and `shadow` rises with CVD. Positive gap ⇒ CVD
 * above price ⇒ amber, in both places. Had the two disagreed, this module would have hung the
 * desk's amber verdict on the code's violet ghost — a wrong badge on a correct chart, and no
 * arithmetic arm anywhere would have seen it, because both sides are internally consistent.
 */
export const H2U_CELLS: readonly ReadingCell[] = [
  { marker: "amber",  form: "percentile", half: "TRAIN",   n:  515, hitRate: 0.369, baseline: 0.386, delta: -0.017, p: 0.747 },
  { marker: "amber",  form: "percentile", half: "HOLDOUT", n:  328, hitRate: 0.351, baseline: 0.401, delta: -0.050, p: 0.956 },
  { marker: "amber",  form: "absolute",   half: "TRAIN",   n: 1291, hitRate: 0.373, baseline: 0.386, delta: -0.012, p: 0.873 },
  { marker: "amber",  form: "absolute",   half: "HOLDOUT", n: 1002, hitRate: 0.400, baseline: 0.401, delta: -0.001, p: 0.528 },
  { marker: "violet", form: "percentile", half: "TRAIN",   n:  420, hitRate: 0.386, baseline: 0.387, delta: -0.002, p: 0.533 },
  { marker: "violet", form: "percentile", half: "HOLDOUT", n:  313, hitRate: 0.371, baseline: 0.387, delta: -0.016, p: 0.786 },
  { marker: "violet", form: "absolute",   half: "TRAIN",   n: 1100, hitRate: 0.414, baseline: 0.387, delta:  0.026, p: 0.038 },
  { marker: "violet", form: "absolute",   half: "HOLDOUT", n:  927, hitRate: 0.365, baseline: 0.387, delta: -0.022, p: 0.968 },
] as const;

/** The desk's provenance, carried so the ink can be traced to the act that authorised it. */
export const PREREG_004_READING = {
  ruling: "R1297",
  desk: "DESK-n43",
  readOn: "2026-09-02",
  contract: "PREREG_004 @552fa9f3",
  receipts: "backtest2/order75-export @75ca76c3",
  document: "docs/research/PREREG_004_DESK_READING_2026-09-02.md",
  alpha: 0.0253,
  seed: 3486367464,
} as const;

/**
 * THE DECLARED VERDICTS. Transcribed from R1297; not computed here.
 *   ghost amber ............ DEAD — both forms fail both halves
 *   ghost violet ........... HYPOTHESIS — one TRAIN cell above baseline, p 0.038, above α
 *   divergence ligatures ... HYPOTHESIS · DISPLAY ONLY — unchanged by this reading
 */
export const VERDICTS: Readonly<Record<string, StudyVerdict>> = {
  "ghost:amber": "DEAD",
  "ghost:violet": "HYPOTHESIS",
  "divergence:ligature": "HYPOTHESIS",
} as const;

/** R1297: *"Nothing flips to CALIBRATED."* Stated over the TABLE, not over a list of instances. */
export const NOTHING_CALIBRATES = (
  verdicts: Readonly<Record<string, StudyVerdict>> = VERDICTS,
): boolean => {
  const values = Object.values(verdicts);
  // ⚠ AN EMPTY TABLE SATISFIES `every` VACUOUSLY. A verdict table that lost its rows would report
  // "nothing calibrates" as confidently as a correct one, so emptiness is a FAILURE, not a pass.
  if (values.length === 0) return false;
  return values.every((verdict) => verdict !== "CALIBRATED");
};

export const cellsFor = (marker: GhostClass, cells: readonly ReadingCell[] = H2U_CELLS): readonly ReadingCell[] =>
  cells.filter((cell) => cell.marker === marker);

/**
 * The cell a HYPOTHESIS rests on: the most favourable one, by Δ against its own baseline.
 * For violet that is absolute · TRAIN · n=1100 (Δ +0.026). For a DEAD marker there is no such
 * cell — every Δ is negative — and that is precisely what `readingIsSelfConsistent` tests.
 */
export const leadingCell = (marker: GhostClass, cells: readonly ReadingCell[] = H2U_CELLS): ReadingCell =>
  cellsFor(marker, cells).reduce((best, cell) => (cell.delta > best.delta ? cell : best));

export const cellsAboveBaseline = (marker: GhostClass, cells: readonly ReadingCell[] = H2U_CELLS): number =>
  cellsFor(marker, cells).filter((cell) => cell.delta > 0).length;

/**
 * ⛔ THE FALSIFIER FOR A TRANSCRIPTION. Returns null when the declared verdict agrees with the
 * cells on R1297's own grounds, or a reason when it does not. It must be capable of BOTH answers:
 * flipping either a verdict or the sign of a Δ has to change what comes back, or the arm is
 * decorative. Driven mutants on both sides are in `prereg004Reading.test.ts`.
 */
export function readingIsSelfConsistent(
  marker: GhostClass,
  verdicts: Readonly<Record<string, StudyVerdict>> = VERDICTS,
  cells: readonly ReadingCell[] = H2U_CELLS,
): string | null {
  const verdict = verdicts[`ghost:${marker}`];
  const mine = cellsFor(marker, cells);
  /**
   * ⛔ A MISSING VERDICT IS A FAILURE, NOT A PASS. Written first, every branch below tested
   * `verdict === …`, so a marker whose row had been dropped from the table matched NONE of them
   * and fell through to `null` — "consistent". That is this seat's most-paid-for shape: the check
   * fails toward COMFORT, and it fails hardest exactly when the table has been damaged. The two
   * emptiness guards here are the same defect in its other form.
   */
  if (verdict === undefined) return `${marker} has NO declared verdict — the reading is missing the row it is being checked against`;
  if (mine.length === 0) return `${marker} carries no cells — the reading cannot be checked against nothing`;
  const above = cellsAboveBaseline(marker, cells);
  if (verdict === "DEAD" && above !== 0) {
    return `${marker} is declared DEAD but ${above} of ${mine.length} cells sit above baseline`;
  }
  if (verdict === "HYPOTHESIS" && above === 0) {
    return `${marker} is declared HYPOTHESIS with no cell above baseline — nothing for the hypothesis to rest on`;
  }
  if (verdict === "HYPOTHESIS" && leadingCell(marker, cells).p <= PREREG_004_READING.alpha) {
    return `${marker}'s leading cell clears α (${leadingCell(marker, cells).p}) — R1297 records it as NOT clearing`;
  }
  return null;
}

/**
 * The qualifier the chip carries beside its verdict.
 *   HYPOTHESIS ⇒ **filter · era · n**, exactly the three facts R1297 names for the violet ghost.
 *   DEAD ⇒ the BREADTH of the kill, counted from the cells rather than asserted, so a reader can
 *   see it is not one unlucky half. Both are computed; neither is a string I chose to believe.
 */
export function ghostQualifier(marker: GhostClass): string {
  const verdict = VERDICTS[`ghost:${marker}`];
  const cells = cellsFor(marker);
  if (verdict === "DEAD") {
    return `both forms · both halves · ${cellsAboveBaseline(marker)} of ${cells.length} above baseline`;
  }
  const lead = leadingCell(marker);
  return `${lead.form} · ${lead.half} · n=${lead.n}`;
}

export const ghostVerdict = (marker: GhostClass): StudyVerdict => VERDICTS[`ghost:${marker}`];

/**
 * What the pane renders for a ghost chip: the verdict, its qualifier, and whether it should take
 * `.chip.hyp` (ink) or the muted base `.chip`.
 *
 * ⚠ A DEAD MARKER IS DE-EMPHASISED, NOT HIDDEN, AND NOT ALARMED. Muted base chip: `var(--muted)`
 * on `var(--chip)`, a pair already carried by the design system in BOTH themes, so this adds no
 * token and cannot trip the law-8 gate. Removing the chip would leave the ghost line drawn with no
 * statement of its status; painting it `.warn` would spend the staleness ink — which means "this
 * feed is broken" — on a study that is merely dead. Neither is what the reading says.
 */
export function ghostBadge(marker: GhostClass): {
  verdict: StudyVerdict;
  qualifier: string;
  emphasis: "ink" | "muted";
} {
  const verdict = ghostVerdict(marker);
  return {
    verdict,
    qualifier: ghostQualifier(marker),
    emphasis: verdict === "HYPOTHESIS" ? "ink" : "muted",
  };
}
